import React from 'react';
import { formatPrice, formatAmount, parseFloatFromString, formatNumberToReadable } from '../utils/formatters';

/**
 * Shared preview panel for exit strategy levels and projected results.
 * Used by ExitStrategyPlanner (variant="full") and ExitStrategyTemplateModal (variant="compact").
 *
 * levels: array of { price, percentage } (price can be number or string)
 * backendPreview: optional { levels?: [{ roi, belowCost }], levelsAtLoss, amountAtLoss, lossAmount, estimatedPnL, estimatedROI, totalSellAmount }
 * variant: 'full' | 'compact' — full shows Market Cap, % of Target, Amount columns
 * context (for full): { selectedBasePrice, targetMultiple, currentPrice, currentMarketCap, previewHoldings, hodlEnabled, hodlPercentage, takeInitialsEnabled }
 * projectedResults: optional fallback { profit, soldPercentage, soldAmount, roiOnSoldPortion, returnMultipleOnSoldPortion }
 * tokenTicker: string
 * children: optional footer (buttons, messages)
 */
const ExitStrategyPreviewPanel = ({
  title = 'Planned Transactions Preview',
  levels = [],
  backendPreview,
  useBackendLevels = false,
  emptyMessage = 'No planned levels to display.',
  variant = 'full',
  context = {},
  projectedResults,
  tokenTicker = 'TOKEN',
  children,
}) => {
  const {
    selectedBasePrice = 0,
    targetMultiple = '3',
    currentPrice = 0,
    currentMarketCap = 0,
    previewHoldings = 1000,
    hodlEnabled = false,
    hodlPercentage = 10,
    takeInitialsEnabled = false,
  } = context;

  const hasLevels = levels && levels.length > 0;

  if (!hasLevels) {
    return (
      <div className="bg-white dark:bg-gray-800">
        <h2 className="text-sm font-medium text-gray-800 dark:text-gray-100 mb-2">{title}</h2>
        <div className="text-gray-600 dark:text-gray-400">{emptyMessage}</div>
        {children}
      </div>
    );
  }

  const targetMult = parseFloatFromString(targetMultiple) || 0;
  const effectiveTargetPrice = selectedBasePrice * targetMult;
  const availableHoldings = hodlEnabled ? previewHoldings * (100 - hodlPercentage) / 100 : previewHoldings;

  return (
    <div className="bg-white dark:bg-gray-800">
      <h2 className="text-sm font-medium text-gray-800 dark:text-gray-100 mb-2">{title}</h2>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-xs">
          <thead>
            <tr className="border-b dark:border-gray-700">
              <th className="text-left py-1 font-medium text-gray-500 dark:text-gray-400">Level</th>
              <th className="text-left py-1 font-medium text-gray-500 dark:text-gray-400">Price</th>
              {variant === 'full' && (
                <>
                  <th className="text-left py-1 font-medium text-gray-500 dark:text-gray-400">Market Cap</th>
                  <th className="text-left py-1 font-medium text-gray-500 dark:text-gray-400">% of Target</th>
                </>
              )}
              <th className="text-left py-1 font-medium text-gray-500 dark:text-gray-400">% to Sell</th>
              {variant === 'full' && (
                <th className="text-left py-1 font-medium text-gray-500 dark:text-gray-400">Amount</th>
              )}
              <th className="text-left py-1 font-medium text-gray-500 dark:text-gray-400">Value</th>
              <th className="text-left py-1 font-medium text-gray-500 dark:text-gray-400">ROI</th>
            </tr>
          </thead>
          <tbody>
            {levels.map((level, index) => {
              const backendLevel = backendPreview?.levels?.[index];
              const price = typeof level.price === 'number' ? level.price : parseFloatFromString(level.price);
              const percentage = typeof level.percentage === 'number' ? level.percentage : parseFloatFromString(level.percentage);
              const isTakeInitials = index === 0 && takeInitialsEnabled;
              const levelName = isTakeInitials ? 'TI' : `${index + (isTakeInitials ? 0 : 1)}`;

              const effectivePercentage = useBackendLevels ? percentage : hodlEnabled ? percentage * (100 - hodlPercentage) / 100 : percentage;
              const levelAmount = useBackendLevels ? parseFloatFromString(backendLevel?.amount) : (percentage / 100) * availableHoldings;
              const levelValue = useBackendLevels ? parseFloatFromString(backendLevel?.value) : levelAmount * price;

              let percentOfTarget = 100;
              if (effectiveTargetPrice > 0) {
                percentOfTarget = (price / effectiveTargetPrice) * 100;
              }
              let estimatedMarketCap = 0;
              if (variant === 'full' && currentPrice > 0 && currentMarketCap > 0) {
                estimatedMarketCap = (price / currentPrice) * currentMarketCap;
              }

              const belowCost = backendLevel?.belowCost;
              const levelRoi = backendLevel?.roi;

              const rowClass = belowCost
                ? 'border-b dark:border-gray-700 hover:bg-red-50 dark:hover:bg-red-900/20 bg-red-50/50 dark:bg-red-900/10 transition duration-150'
                : 'border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition duration-150';

              return (
                <tr key={index} className={rowClass}>
                  <td className="py-2 text-gray-800 dark:text-gray-200">
                    {levelName}
                    {belowCost && <span className="ml-1 text-red-500" title="Below cost basis">⚠</span>}
                  </td>
                  <td className="py-2 text-gray-800 dark:text-gray-200">{formatPrice(price)}</td>
                  {variant === 'full' && (
                    <>
                      <td className="py-2 text-gray-800 dark:text-gray-200">{formatNumberToReadable(estimatedMarketCap)}</td>
                      <td className="py-2 text-gray-800 dark:text-gray-200">{percentOfTarget.toFixed(1)}%</td>
                    </>
                  )}
                  <td className="py-2 text-gray-800 dark:text-gray-200">{effectivePercentage.toFixed(1)}%</td>
                  {variant === 'full' && (
                    <td className="py-2 text-gray-800 dark:text-gray-200">{formatAmount(levelAmount)}</td>
                  )}
                  <td className="py-2 text-gray-800 dark:text-gray-200">{formatPrice(levelValue)}</td>
                  <td className={`py-2 ${belowCost ? 'text-red-600 dark:text-red-400' : levelRoi !== undefined && levelRoi > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                    {levelRoi !== undefined ? `${levelRoi.toFixed(1)}%` : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Warning for levels below cost */}
        {backendPreview?.levelsAtLoss > 0 && (
          <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-md">
            <div className="flex items-start">
              <span className="text-amber-500 mr-2 text-lg">⚠️</span>
              <div className="text-sm">
                <div className="font-medium text-amber-800 dark:text-amber-200">
                  {backendPreview.levelsAtLoss} level{backendPreview.levelsAtLoss > 1 ? 's' : ''} below cost basis
                </div>
                <div className="text-amber-700 dark:text-amber-300 mt-1">
                  Selling {formatAmount(parseFloatFromString(backendPreview.amountAtLoss))} tokens at these levels would result in a loss of {formatPrice(parseFloatFromString(backendPreview.lossAmount))}.
                </div>
                <div className="text-amber-600 dark:text-amber-400 mt-1 text-xs">
                  This may be intentional for tax loss harvesting or risk management.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Projected Results */}
        <div className="mt-6 dark:border-gray-700 pt-4">
          <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">Projected Results</h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
            {backendPreview ? 'Calculated using FIFO cost basis.' : 'Estimates based on average cost. Saved values use FIFO cost basis and may differ slightly.'}
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
              <div className="text-sm text-gray-500 dark:text-gray-400">Est. PnL</div>
              <div className={`text-lg font-semibold ${
                backendPreview && parseFloatFromString(backendPreview.estimatedPnL) < 0
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-gray-800 dark:text-gray-200'
              }`}>
                {backendPreview
                  ? formatPrice(parseFloatFromString(backendPreview.estimatedPnL))
                  : projectedResults && formatPrice(projectedResults.profit)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {backendPreview ? (
                  `${parseFloatFromString(backendPreview.estimatedROI).toFixed(1)}% ROI`
                ) : (
                  projectedResults?.returnMultipleOnSoldPortion > 1 && (
                    `${(projectedResults.roiOnSoldPortion * 100).toFixed(1)}% ROI (${projectedResults.returnMultipleOnSoldPortion.toFixed(1)}x)`
                  )
                )}
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
              <div className="text-sm text-gray-500 dark:text-gray-400">Sell Amount</div>
              <div className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                {hodlEnabled ? `${(100 - hodlPercentage).toFixed(1)}% of holdings` : projectedResults && `${projectedResults.soldPercentage.toFixed(1)}% of holdings`}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {backendPreview
                  ? `${formatAmount(parseFloatFromString(backendPreview.totalSellAmount))} ${tokenTicker}`
                  : projectedResults && `${projectedResults.soldAmount.toFixed(6)} ${tokenTicker}`}
              </div>
            </div>
          </div>
        </div>

        {children && (
          <div className="mt-4 flex justify-between items-center">
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExitStrategyPreviewPanel;
